import {
  faBell,
  faBox,
  faCoins,
  faCrown,
  faFileAlt,
  faGavel,
  faGlobe,
  faHome,
  faImages,
  faInfo,
  faLifeRing,
  faList,
  faNewspaper,
  faSearch,
  faShieldAlt,
  faSign,
  faStopwatch,
  faStore,
  faTachometer,
  faUserCrown,
} from '@fortawesome/pro-light-svg-icons'

import { generateApplicationUrl } from '@domain-group/javascript-utils'

import useCurrentUser from '@hooks/useCurrentUser'
import type { UseEntityImpersonationPayload } from '@hooks/useEntityImpersonation'
import type { UseLoginAsUserPayload } from '@hooks/useLoginAsUser'

import LogoNavIcon from '@components/LogoNavIcon'

type GenerateMainNavigationItemsParams = {
  callbacks: {
    loginAsUser: UseLoginAsUserPayload['callbacks']['loginAsUser'],
    unlinkImpersonation?: UseEntityImpersonationPayload['callbacks']['unlinkImpersonation'],
  },
  currentUserPayload: ReturnType<typeof useCurrentUser>,
  hasMultipleAgencies: boolean,
  intercomEnabled?: boolean,
  selectedAgencyId: number,
}

type ConstructAgencyAdminUrlParams = {
  agencyAdminPagesPermissions: boolean,
  agencyAdminUserPermissions: boolean,
  agencyId: number,
  association_id: number,
  hasMultipleAgencies: boolean,
  userId: number,
}

const constructAgencyAdminUrl = (params: ConstructAgencyAdminUrlParams) => {
  const {
    agencyAdminPagesPermissions, agencyAdminUserPermissions, agencyId, association_id, hasMultipleAgencies, userId,
  } = params

  if (!agencyAdminPagesPermissions && !agencyAdminUserPermissions){
    return `#/agencies/${association_id}/manage/users/${userId}/edit`
  }

  if (!agencyAdminPagesPermissions && agencyAdminUserPermissions){
    return `#/agencies/${association_id}/manage/users`
  }

  return (hasMultipleAgencies)
    ? `#/agencies/${agencyId}/manage/agencies`
    : `#/agencies/${agencyId}/manage/details`
}

const generateMainNavigationItems = (params: GenerateMainNavigationItemsParams) => {
  const {
    callbacks,
    currentUserPayload,
    hasMultipleAgencies,
    intercomEnabled,
    selectedAgencyId,
  } = params

  const { loginAsUser, unlinkImpersonation } = callbacks || {}

  const {
    agency,
    callbacks: {
      loginToExternalPlatform,
    },
    currentUser,
    impersonatedUser,
    impersonatingUser,
    isAgencyAdmin,
    isImpersonatingUser,
    permissions,
  } = currentUserPayload

  const { association_id, id } = currentUser
  const agencyId = selectedAgencyId || association_id
  const overviewsAgencyParam = hasMultipleAgencies ? 'all' : agencyId

  // @ts-expect-error TS2339
  const { applicationUrl: engageUrl } = generateApplicationUrl({
    applicationKey: 'engage',
    // @ts-expect-error TS2322
    environment: process.env.REACT_APP_APPLICATION_ENV,
  })

  const usesCampaignManagement = agency?.features?.campaign_management ?? true
  const publicationManagerVersion = agency?.options?.publication_planner ?? 'V1'

  const agencyAdminPagesPermissions: boolean = permissions?.agency?.admin?.agency_admin_pages?.manage !== undefined
   && permissions?.agency?.admin?.agency_admin_pages?.manage !== null
    ? permissions?.agency?.admin?.agency_admin_pages?.manage
    : true

  const agencyAdminUserPermissions: boolean = permissions?.agency?.admin?.users?.manage

  return {
    sections: [
      {
        key: 'dashboard',
        visible: true,
        items: [
          {
            href: '#/',
            icon: faTachometer,
            key: 'dashboard',
            title: 'Dashboard',
            visible: true,
          },
        ],
      },
      {
        key: 'marketing',
        title: 'Marketing',
        visible: true,
        items: [
          {
            href: '#/agency/marketing',
            icon: faCrown,
            key: 'agency-marketing',
            title: 'Agency Marketing',
            visible: usesCampaignManagement,
          },
          {
            icon: faList,
            key: 'overviews',
            title: 'Overviews',
            visible: usesCampaignManagement,
            items: [
              {
                href: `#/agency/dashboard/agencies/${overviewsAgencyParam}/overview/auctions`,
                icon: faGavel,
                key: 'overview-auctions',
                title: 'Auctions',
                visible: true,
              },
              {
                href: `#/agency/dashboard/agencies/${overviewsAgencyParam}/overview/campaigns`,
                icon: faHome,
                key: 'overview-campaigns',
                title: 'Campaigns',
                visible: true,
              },
              {
                href: `#/agency/dashboard/agencies/${overviewsAgencyParam}/overview/deadlines`,
                icon: faStopwatch,
                key: 'overview-deadlines',
                title: 'Deadlines',
                visible: true,
              },
              {
                href: `#/agency/dashboard/agencies/${overviewsAgencyParam}/overview/inspections`,
                icon: faSearch,
                key: 'overview-inspections',
                title: 'Inspections',
                visible: true,
              },
              {
                href: `#/agency/dashboard/agencies/${overviewsAgencyParam}/overview/orders`,
                icon: faBox,
                key: 'overview-orders',
                title: 'Orders',
                visible: true,
              },
              {
                href: `#/agency/dashboard/agencies/${overviewsAgencyParam}/overview/portalSyncs`,
                icon: faGlobe,
                key: 'overview-portal-syncs',
                title: 'Portal Syncs',
                visible: true,
              },
              {
                href: `#/agency/dashboard/agencies/${overviewsAgencyParam}/overview/quotes`,
                icon: faFileAlt,
                key: 'overview-quotes',
                title: 'Quotes',
                visible: true,
              },
              {
                href: `#/agency/dashboard/agencies/${overviewsAgencyParam}/overview/signboards`,
                icon: faSign,
                key: 'overview-signboards',
                title: 'Signboards',
                visible: true,
              },
            ],
          },
          {
            icon: faNewspaper,
            key: 'publications',
            title: 'Publications',
            visible: usesCampaignManagement,
            items: [
              {
                href: publicationManagerVersion === 'V2' ? '#/publications/bookingPlanner' : undefined,
                icon: faImages,
                key: 'media-planner',
                onClick: publicationManagerVersion === 'V1'
                  ? () => loginAsUser({
                    allowed_pages: 'publications',
                    redirect: '/agency/publications/manage#/',
                  })
                  : undefined,
                title: 'Planner',
                visible: true,
              },
            ],
          },
        ],
      },
      {
        key: 'admin',
        title: 'Admin',
        visible: true,
        items: [
          {
            href: `#/agencies/${agencyId}/manage/accounting/invoices`,
            icon: faCoins,
            key: 'accounting',
            title: 'Accounting',
            visible: usesCampaignManagement,
          },
          {
            href: constructAgencyAdminUrl({
              agencyAdminPagesPermissions,
              agencyAdminUserPermissions,
              agencyId: agencyId!,
              association_id: association_id!,
              hasMultipleAgencies,
              userId: id!,
            }),
            icon: faStore,
            key: 'agency-admin',
            title: 'Agency Admin',
            visible: isAgencyAdmin,
          },
          {
            href: `#/agencies/${association_id}/manage/users/${id}/edit`,
            icon: faShieldAlt,
            key: 'account-settings',
            title: 'Account Settings',
            visible: !isAgencyAdmin,
          },
          {
            href: '#/activity-logs',
            icon: faBell,
            key: 'activities',
            title: 'Activities',
            visible: isAgencyAdmin,
          },
        ],
      },
      {
        key: 'support',
        title: 'Support',
        visible: true,
        items: [
          {
            href: intercomEnabled ? 'https://intercom.help/realhub/en' : 'https://realhub.freshdesk.com/support/home',
            icon: faLifeRing,
            key: 'training',
            linkProps: {
              target: 'blank',
            },
            title: 'Training',
            visible: true,
          },
          {
            href: 'https://intercom.news/realhub_updates',
            icon: faInfo,
            key: 'product-news',
            linkProps: {
              target: 'blank',
            },
            title: 'Product News',
            visible: true,
          },
        ],
      },
      {
        key: 'external',
        title: 'External',
        visible: true,
        items: [
          {
            iconComponent: LogoNavIcon('engage', 'rgba(255, 255, 255, 0.7)'),
            key: 'engage',
            onClick: () => loginToExternalPlatform({ path: engageUrl, application_key: 'engage' }),
            title: 'Engage',
            visible: true,
          },
        ],
      },
      {
        key: 'impersonation',
        title: 'Impersonating',
        visible: isImpersonatingUser,
        items: [
          {
            icon: faUserCrown,
            key: 'user',
            title: impersonatedUser.full_name,
            visible: true,
          },
          {
            icon: faHome,
            key: 'back-to-admin',
            // @ts-expect-error TS2322
            onClick: unlinkImpersonation ? () => unlinkImpersonation({ userId: impersonatingUser?.id }) : undefined,
            title: 'Back to Admin',
            visible: true,
          },
        ],
      },
    ],
  }
}

export default generateMainNavigationItems
